import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  createKonturPayment,
  deleteKonturPayment,
  fetchClientProfiles,
  fetchKonturClients,
  getApiErrorMessage,
  updateClientProfile,
  updateKonturComment,
  updateKonturPayment,
} from "../api";
import ErrorRetry from "../components/ErrorRetry";
import Loading from "../components/Loading";

const STATUS_LABELS = {
  unpaid: "Не оплачен",
  overdue: "Просрочен",
  expiring: "Скоро истекает",
  paid: "Оплачен",
};

const STAT_FILTERS = [
  { value: "", label: "Всего", key: "total" },
  { value: "due", label: "Должны", key: "debt" },
  { value: "paid", label: "Оплачено", key: "paid" },
  { value: "expiring", label: "Скоро", key: "expiring" },
  { value: "overdue", label: "Просрочено", key: "overdue" },
  { value: "unpaid", label: "Не оплачен", key: "unpaid" },
  { value: "does_not_pay", label: "Не платит", key: "does_not_pay" },
];

const DEFAULT_AMOUNT = "5000";

const Icons = {
  Search: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="8"></circle>
      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
    </svg>
  ),
  Plus: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="12" y1="5" x2="12" y2="19"></line>
      <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
  ),
  Empty: () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="5" width="20" height="14" rx="2"></rect>
      <line x1="2" y1="10" x2="22" y2="10"></line>
    </svg>
  ),
};

const DATE_FORMAT = new Intl.DateTimeFormat("ru-RU", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

const AMOUNT_FORMAT = new Intl.NumberFormat("ru-RU", {
  style: "currency",
  currency: "RUB",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

function formatDate(value) {
  if (!value) return "—";
  const [year, month, day] = String(value).split("-");
  if (!year || !month || !day) return value;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  if (Number.isNaN(date.getTime())) return value;
  return DATE_FORMAT.format(date);
}

function formatAmount(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return AMOUNT_FORMAT.format(num);
}

function previewComment(value, max = 80) {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (!text) return "";
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}

function parseOpenIds(raw) {
  return new Set(
    String(raw || "")
      .split(",")
      .map((value) => Number(value))
      .filter((id) => Number.isFinite(id) && id > 0)
  );
}

function patchParams(prev, patch) {
  const next = new URLSearchParams(prev);
  Object.entries(patch).forEach(([key, value]) => {
    if (value == null || value === "") next.delete(key);
    else next.set(key, String(value));
  });
  return next;
}

function todayIso() {
  return new Date().toLocaleDateString("en-CA", { timeZone: "Europe/Moscow" });
}

function PaymentForm({ form, setForm, disabled, submitLabel, onSubmit, onCancel, maxDate, idPrefix }) {
  const paidAtRef = useRef(null);
  const amountRef = useRef(null);
  const [localError, setLocalError] = useState("");
  const [errorField, setErrorField] = useState("");
  const paidAtErrorId = `${idPrefix}-paid-at-error`;
  const amountErrorId = `${idPrefix}-amount-error`;

  return (
    <form
      className="kp-pay-form"
      onSubmit={(e) => {
        e.preventDefault();
        const amount = Number(form.amount);
        if (!form.paid_at) {
          setLocalError("Укажите дату оплаты");
          setErrorField("paid_at");
          paidAtRef.current?.focus();
          return;
        }
        if (!Number.isFinite(amount) || amount <= 0) {
          setLocalError("Укажите сумму больше нуля");
          setErrorField("amount");
          amountRef.current?.focus();
          return;
        }
        if (form.paid_at > maxDate) {
          setLocalError("Дата оплаты не может быть в будущем");
          setErrorField("paid_at");
          paidAtRef.current?.focus();
          return;
        }
        setLocalError("");
        setErrorField("");
        onSubmit();
      }}
    >
      <label className="login-label">
        Дата оплаты
        <input
          ref={paidAtRef}
          className="login-input"
          type="date"
          name="paid_at"
          autoComplete="off"
          value={form.paid_at}
          onChange={(e) => setForm({ ...form, paid_at: e.target.value })}
          max={maxDate}
          required
          disabled={disabled}
          aria-invalid={errorField === "paid_at"}
          aria-describedby={errorField === "paid_at" ? paidAtErrorId : undefined}
        />
        {errorField === "paid_at" && localError && (
          <span className="cd-field-error" id={paidAtErrorId} role="status">{localError}</span>
        )}
      </label>
      <label className="login-label">
        Сумма,{"\u00A0"}₽
        <input
          ref={amountRef}
          className="login-input"
          type="number"
          name="amount"
          autoComplete="off"
          inputMode="decimal"
          min="0.01"
          step="0.01"
          value={form.amount}
          onChange={(e) => setForm({ ...form, amount: e.target.value })}
          required
          disabled={disabled}
          aria-invalid={errorField === "amount"}
          aria-describedby={errorField === "amount" ? amountErrorId : undefined}
        />
        {errorField === "amount" && localError && (
          <span className="cd-field-error" id={amountErrorId} role="status">{localError}</span>
        )}
      </label>
      <div className="kp-pay-actions">
        <button type="submit" className="login-button" disabled={disabled}>
          {submitLabel}
        </button>
        {onCancel && (
          <button type="button" className="cd-filter-reset" onClick={onCancel} disabled={disabled}>
            Отмена
          </button>
        )}
      </div>
    </form>
  );
}

function ClientRow({
  item,
  expanded,
  onToggle,
  payForm,
  setPayForm,
  saving,
  onCreatePayment,
  commentForm,
  setCommentForm,
  onSaveComment,
  editingId,
  editForm,
  setEditForm,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onAskDelete,
  onRemoveClient,
  onToggleDoesNotPay,
}) {
  const commentPreview = previewComment(item.comment);
  const doesNotPay = Boolean(item.does_not_pay);
  return (
    <div className={`cd-row ${expanded ? "cd-row--expanded" : ""}`}>
      <div className="cp-row-head">
        <button
          type="button"
          className="cd-row-main"
          aria-expanded={expanded}
          aria-label={`${expanded ? "Свернуть" : "Раскрыть"} ${item.name}`}
          onClick={() => onToggle(item.client_id)}
        >
          <span className="cd-row-chevron" aria-hidden="true">{expanded ? "▾" : "▸"}</span>
          <div className="cd-row-body">
            <div className="cd-row-top">
              <span className="cd-row-name">{item.name}</span>
              <span className={`kp-badge kp-badge--${item.status}`}>{STATUS_LABELS[item.status]}</span>
            </div>
            <div className="cd-row-meta">
              {item.inn ? `ИНН ${item.inn}` : "ИНН не указан"}
              {" · "}
              оплачено до {formatDate(item.paid_until)}
              {item.last_amount != null && ` · последняя ${formatAmount(item.last_amount)}`}
              {item.days_left != null && item.status !== "unpaid" && (
                item.days_left >= 0
                  ? ` · осталось ${item.days_left} дн.`
                  : ` · просрочено ${Math.abs(item.days_left)} дн.`
              )}
              {Number(item.amount_due) > 0 && ` · долг ${formatAmount(item.amount_due)}`}
              {doesNotPay ? " · не платит" : ""}
              {commentPreview ? (
                <span className="cp-comment-preview">{commentPreview}</span>
              ) : null}
            </div>
          </div>
        </button>
        <div className="kp-other-payer">
          <label className="kp-other-payer-check">
            <input
              type="checkbox"
              name={`does_not_pay_${item.client_id}`}
              checked={doesNotPay}
              disabled={saving}
              aria-label={`${item.name}: не платит`}
              onChange={(e) => onToggleDoesNotPay(item, e.target.checked)}
            />
            Не платит
          </label>
        </div>
      </div>
      {expanded && (
        <div className="cd-row-expand kp-expand">
          <div className="kp-add-block">
            <h3 className="kp-history-title">Комментарий</h3>
            <form
              className="cp-comment-form"
              onSubmit={(e) => {
                e.preventDefault();
                onSaveComment(item.client_id);
              }}
            >
              <label className="login-label">
                Комментарий
                <textarea
                  className="login-input"
                  name="comment"
                  autoComplete="off"
                  rows={3}
                  maxLength={1000}
                  value={commentForm}
                  onChange={(e) => setCommentForm(e.target.value)}
                  disabled={saving}
                />
              </label>
              <div className="kp-pay-actions">
                <button type="submit" className="login-button" disabled={saving}>
                  Сохранить
                </button>
              </div>
            </form>
          </div>
          <div className="kp-history">
            <h3 className="kp-history-title">История оплат</h3>
            {item.payments.length === 0 ? (
              <p className="kp-history-empty">Оплат ещё нет</p>
            ) : (
              <ul className="kp-history-list">
                {item.payments.map((payment) => (
                  <li key={payment.id} className="kp-history-item">
                    {editingId === payment.id ? (
                      <PaymentForm
                        form={editForm}
                        setForm={setEditForm}
                        disabled={saving}
                        submitLabel="Сохранить оплату"
                        onSubmit={() => onSaveEdit(payment.id)}
                        onCancel={onCancelEdit}
                        maxDate={todayIso()}
                        idPrefix={`edit-${payment.id}`}
                      />
                    ) : (
                      <>
                        <div className="kp-history-main">
                          <span>{formatDate(payment.paid_at)}</span>
                          <span>{formatAmount(payment.amount)}</span>
                        </div>
                        <div className="kp-history-actions">
                          <button
                            type="button"
                            className="cd-sort-btn"
                            onClick={() => onStartEdit(payment)}
                          >
                            Изменить
                          </button>
                          <button
                            type="button"
                            className="cd-danger-btn"
                            onClick={() => onAskDelete(payment)}
                          >
                            Удалить
                          </button>
                        </div>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="kp-add-block">
            <h3 className="kp-history-title">Отметить оплату</h3>
            <PaymentForm
              form={payForm}
              setForm={setPayForm}
              disabled={saving}
              submitLabel="Записать оплату"
              onSubmit={() => onCreatePayment(item.client_id)}
              maxDate={todayIso()}
              idPrefix={`pay-${item.client_id}`}
            />
            <button
              type="button"
              className="cd-filter-reset"
              disabled={saving}
              onClick={() => onRemoveClient(item)}
            >
              Убрать из <span translate="no">Контура</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function KonturPayments() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const statusFilter = searchParams.get("status") || "";
  const expanded = useMemo(() => parseOpenIds(searchParams.get("open")), [searchParams]);
  const updateParams = useCallback((patch) => {
    setSearchParams((prev) => patchParams(prev, patch), { replace: true });
  }, [setSearchParams]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [payForms, setPayForms] = useState({});
  const [commentForms, setCommentForms] = useState({});
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ paid_at: "", amount: DEFAULT_AMOUNT });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [candidateQuery, setCandidateQuery] = useState("");
  const [candidatesLoading, setCandidatesLoading] = useState(false);

  const loadItems = useCallback(async ({ soft = false } = {}) => {
    if (!soft) setLoading(true);
    setError("");
    try {
      const data = await fetchKonturClients();
      setItems(data);
    } catch (err) {
      setError(getApiErrorMessage(err, "Не удалось загрузить оплаты Контура"));
    } finally {
      if (!soft) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    if (!addOpen && !deleteTarget) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      setAddOpen(false);
      setDeleteTarget(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [addOpen, deleteTarget]);

  const tracked = useMemo(
    () => items.filter((item) => !item.does_not_pay),
    [items]
  );

  const stats = useMemo(() => {
    const counts = {
      unpaid: 0,
      overdue: 0,
      expiring: 0,
      paid: 0,
      does_not_pay: 0,
    };
    let debt = 0;
    items.forEach((item) => {
      if (item.does_not_pay) {
        counts.does_not_pay += 1;
        return;
      }
      counts[item.status] += 1;
      const due = Number(item.amount_due);
      if (Number.isFinite(due) && due > 0) debt += due;
    });
    return { ...counts, debt };
  }, [items]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return items.filter((item) => {
      const doesNotPay = Boolean(item.does_not_pay);
      if (statusFilter === "does_not_pay") {
        if (!doesNotPay) return false;
      } else if (doesNotPay) {
        return false;
      } else if (statusFilter === "due") {
        if (!(Number(item.amount_due) > 0)) return false;
      } else if (statusFilter && item.status !== statusFilter) {
        return false;
      }
      if (!needle) return true;
      const inn = (item.inn || "").toLowerCase();
      const comment = (item.comment || "").toLowerCase();
      return (
        item.name.toLowerCase().includes(needle)
        || inn.includes(needle)
        || comment.includes(needle)
      );
    });
  }, [items, query, statusFilter]);

  const payDirty = editingId != null || Object.values(payForms).some(
    (form) => form && (form.paid_at !== todayIso() || String(form.amount) !== DEFAULT_AMOUNT)
  );

  useEffect(() => {
    if (!payDirty) return undefined;
    const onBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [payDirty]);

  const toggleExpanded = (clientId) => {
    const next = new Set(expanded);
    if (next.has(clientId)) next.delete(clientId);
    else next.add(clientId);
    updateParams({ open: [...next].join(",") });
    setPayForms((prev) => (
      prev[clientId]
        ? prev
        : { ...prev, [clientId]: { paid_at: todayIso(), amount: DEFAULT_AMOUNT } }
    ));
  };

  const setPayForm = (clientId, form) => {
    setPayForms((prev) => ({ ...prev, [clientId]: form }));
  };

  const setCommentForm = (clientId, value) => {
    setCommentForms((prev) => ({ ...prev, [clientId]: value }));
  };

  const handleSaveComment = async (clientId) => {
    const raw = commentForms[clientId];
    const item = items.find((row) => row.client_id === clientId);
    const comment = raw == null ? (item?.comment || "") : raw;
    setSaving(true);
    setActionError("");
    try {
      await updateKonturComment(clientId, { comment });
      setCommentForms((prev) => {
        const next = { ...prev };
        delete next[clientId];
        return next;
      });
      await loadItems({ soft: true });
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось сохранить комментарий"));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleDoesNotPay = async (item, doesNotPay) => {
    setSaving(true);
    setActionError("");
    try {
      await updateKonturComment(item.client_id, { does_not_pay: doesNotPay });
      await loadItems({ soft: true });
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось сохранить признак оплаты"));
    } finally {
      setSaving(false);
    }
  };

  const handleCreatePayment = async (clientId) => {
    const form = payForms[clientId] || { paid_at: todayIso(), amount: DEFAULT_AMOUNT };
    const amount = Number(form.amount);
    if (!form.paid_at || !Number.isFinite(amount) || amount <= 0) {
      setActionError("Укажите дату и сумму больше нуля");
      return;
    }
    if (form.paid_at > todayIso()) {
      setActionError("Дата оплаты не может быть в будущем");
      return;
    }
    setSaving(true);
    setActionError("");
    try {
      await createKonturPayment({
        client_id: clientId,
        paid_at: form.paid_at,
        amount,
      });
      setPayForms((prev) => ({
        ...prev,
        [clientId]: { paid_at: todayIso(), amount: DEFAULT_AMOUNT },
      }));
      await loadItems({ soft: true });
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось записать оплату"));
    } finally {
      setSaving(false);
    }
  };

  const handleStartEdit = (payment) => {
    setEditingId(payment.id);
    setEditForm({
      paid_at: payment.paid_at,
      amount: String(payment.amount),
    });
  };

  const handleSaveEdit = async (paymentId) => {
    const amount = Number(editForm.amount);
    if (!editForm.paid_at || !Number.isFinite(amount) || amount <= 0) {
      setActionError("Укажите дату и сумму больше нуля");
      return;
    }
    if (editForm.paid_at > todayIso()) {
      setActionError("Дата оплаты не может быть в будущем");
      return;
    }
    setSaving(true);
    setActionError("");
    try {
      await updateKonturPayment(paymentId, {
        paid_at: editForm.paid_at,
        amount,
      });
      setEditingId(null);
      await loadItems({ soft: true });
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось изменить оплату"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    setActionError("");
    try {
      await deleteKonturPayment(deleteTarget.id);
      setDeleteTarget(null);
      await loadItems({ soft: true });
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось удалить оплату"));
    } finally {
      setSaving(false);
    }
  };

  const openAddClient = async () => {
    setAddOpen(true);
    setCandidateQuery("");
    setCandidatesLoading(true);
    setActionError("");
    try {
      const profiles = await fetchClientProfiles("active");
      setCandidates(profiles.filter((p) => !p.uses_kontur));
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось загрузить клиентов"));
      setAddOpen(false);
    } finally {
      setCandidatesLoading(false);
    }
  };

  const handleRemoveClient = async (item) => {
    if (!window.confirm(`Убрать «${item.name}» из учёта Контура? История оплат останется.`)) {
      return;
    }
    setSaving(true);
    setActionError("");
    try {
      await updateClientProfile(item.client_id, { uses_kontur: false });
      await loadItems({ soft: true });
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось убрать клиента"));
    } finally {
      setSaving(false);
    }
  };

  const handleAddClient = async (profile) => {
    setSaving(true);
    setActionError("");
    try {
      await updateClientProfile(profile.id, { uses_kontur: true });
      setAddOpen(false);
      await loadItems({ soft: true });
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось добавить клиента"));
    } finally {
      setSaving(false);
    }
  };

  const filteredCandidates = useMemo(() => {
    const needle = candidateQuery.trim().toLowerCase();
    if (!needle) return candidates;
    return candidates.filter((p) => {
      const inn = (p.inn || "").toLowerCase();
      return p.name.toLowerCase().includes(needle) || inn.includes(needle);
    });
  }, [candidates, candidateQuery]);

  return (
    <div className="page-container cd-page kp-page">
      <div className="page-header">
        <h2>Оплаты <span translate="no">Контура</span></h2>
        <p className="page-subtitle">Годовые оплаты за кабинет отчётности, 5000{"\u00A0"}₽</p>
      </div>

      {error && <ErrorRetry message={error} onRetry={() => loadItems()} />}
      {actionError && !addOpen && !deleteTarget && (
        <div className="login-error" role="status" aria-live="polite">{actionError}</div>
      )}

      <div className="cd-toolbar">
        <div className="cd-search">
          <Icons.Search />
          <input
            type="search"
            name="q"
            autoComplete="off"
            aria-label="Поиск по названию, ИНН или комментарию"
            placeholder="Поиск по названию, ИНН или комментарию…"
            value={query}
            onChange={(e) => updateParams({ q: e.target.value })}
          />
        </div>
        <button type="button" className="cd-add-btn" onClick={openAddClient}>
          <Icons.Plus />
          Добавить клиента
        </button>
      </div>

      <div className="cd-stats" role="group" aria-label="Фильтр по статусу">
        {STAT_FILTERS.map((stat) => {
          const active = statusFilter === stat.value;
          const value = stat.key === "total"
            ? tracked.length
            : stat.key === "debt"
              ? formatAmount(stats.debt)
              : stats[stat.key];
          return (
            <button
              key={stat.value || "all"}
              type="button"
              className={`cd-stat ${stat.key === "debt" ? "cd-stat--debt" : ""} ${active ? "cd-stat--active" : ""}`}
              aria-pressed={active}
              onClick={() => updateParams({ status: active ? "" : stat.value })}
            >
              {stat.label} <strong>{value}</strong>
            </button>
          );
        })}
      </div>

      {loading ? (
        <Loading />
      ) : visible.length === 0 ? (
        <div className="cd-empty">
          <Icons.Empty />
          <p>
            {items.length === 0
              ? <>Нет клиентов с кабинетом <span translate="no">Контура</span>. Добавьте клиента здесь или поставьте флаг в справочнике.</>
              : "Ничего не найдено по текущему фильтру"}
          </p>
        </div>
      ) : (
        <ul className="cd-list">
          {visible.map((item) => (
            <li key={item.client_id}>
              <ClientRow
                item={item}
                expanded={expanded.has(item.client_id)}
                onToggle={toggleExpanded}
                payForm={payForms[item.client_id] || { paid_at: todayIso(), amount: DEFAULT_AMOUNT }}
                setPayForm={(form) => setPayForm(item.client_id, form)}
                saving={saving}
                onCreatePayment={handleCreatePayment}
                commentForm={commentForms[item.client_id] ?? (item.comment || "")}
                setCommentForm={(value) => setCommentForm(item.client_id, value)}
                onSaveComment={handleSaveComment}
                editingId={editingId}
                editForm={editForm}
                setEditForm={setEditForm}
                onStartEdit={handleStartEdit}
                onCancelEdit={() => setEditingId(null)}
                onSaveEdit={handleSaveEdit}
                onAskDelete={setDeleteTarget}
                onRemoveClient={handleRemoveClient}
                onToggleDoesNotPay={handleToggleDoesNotPay}
              />
            </li>
          ))}
        </ul>
      )}

      {addOpen && (
        <div className="users-modal-backdrop" onClick={() => setAddOpen(false)}>
          <div
            className="users-modal users-modal-wide"
            role="dialog"
            aria-modal="true"
            aria-labelledby="kp-add-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="kp-add-title">Добавить клиента в <span translate="no">Контур</span></h3>
            {actionError && <div className="login-error" role="status" aria-live="polite">{actionError}</div>}
            <label className="login-label">
              Поиск
              <input
                className="login-input"
                type="search"
                name="candidate_q"
                autoComplete="off"
                value={candidateQuery}
                onChange={(e) => setCandidateQuery(e.target.value)}
                placeholder="Название или ИНН…"
              />
            </label>
            {candidatesLoading ? (
              <Loading />
            ) : filteredCandidates.length === 0 ? (
              <p className="kp-history-empty">
                Нет активных клиентов без флага. Поставьте «Кабинет <span translate="no">Контура</span>» в справочнике.
              </p>
            ) : (
              <ul className="kp-candidate-list">
                {filteredCandidates.map((profile) => (
                  <li key={profile.id}>
                    <button
                      type="button"
                      className="kp-candidate-btn"
                      disabled={saving}
                      onClick={() => handleAddClient(profile)}
                    >
                      <span>{profile.name}</span>
                      {profile.inn && <span className="kp-candidate-inn">ИНН {profile.inn}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="users-modal-actions">
              <button type="button" className="cd-filter-reset" onClick={() => setAddOpen(false)}>
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="users-modal-backdrop" onClick={() => setDeleteTarget(null)}>
          <div
            className="users-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="kp-del-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="kp-del-title">Удалить оплату?</h3>
            <p>
              {formatDate(deleteTarget.paid_at)} · {formatAmount(deleteTarget.amount)}
            </p>
            {actionError && <div className="login-error" role="status" aria-live="polite">{actionError}</div>}
            <div className="users-modal-actions">
              <button type="button" className="cd-filter-reset" onClick={() => setDeleteTarget(null)}>
                Отмена
              </button>
              <button
                type="button"
                className="cd-danger-btn"
                disabled={saving}
                onClick={handleDelete}
              >
                Удалить оплату
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
